import React, { useEffect, useState, useCallback } from 'react';
import { auditAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../lib/timeFormat';

const ACTION_LABELS = {
  'login': 'Login',
  'user.create': 'User Created',
  'user.update': 'User Updated',
  'user.delete': 'User Deleted',
  'user.reset_password': 'Password Reset (Admin)',
  'user.change_own_password': 'Password Changed (Self)',
  'user.toggle_active': 'User Activated/Deactivated',
  'role.permissions_update': 'Role Permissions Changed',
  'field_visibility.update': 'Field Visibility Changed',
  'lead.assign': 'Lead Assigned',
  'lead.status_change': 'Lead Status Changed',
  'project.assign_architect': 'Architect Assigned',
  'project.status_change': 'Project Status Changed',
  'settings.lead_status_create': 'Lead Status Created',
  'settings.lead_status_update': 'Lead Status Updated',
  'settings.lead_status_delete': 'Lead Status Deleted',
};

export const ActivityLogPage = () => {
  const { user } = useAuth();
  const timeFormat = user?.time_format || '12h';
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterOptions, setFilterOptions] = useState({ actions: [], modules: [] });
  const [filters, setFilters] = useState({ action: '', module: '', date_from: '', date_to: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await auditAPI.list(params);
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load activity log');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    auditAPI.filters().then(({ data }) => setFilterOptions(data)).catch(() => {});
  }, []);

  const setF = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));
  const resetFilters = () => setFilters({ action: '', module: '', date_from: '', date_to: '' });

  return (
    <div className="p-6 space-y-6" data-testid="activity-log-page">
      <div>
        <h1 className="text-2xl font-outfit font-semibold">Activity Log</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Immutable audit trail of important system actions.</p>
      </div>

      <div className="clay-card p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Action</label>
          <select className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900" value={filters.action} onChange={(e) => setF('action', e.target.value)} data-testid="activity-filter-action">
            <option value="">All</option>
            {filterOptions.actions.map((a) => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Module</label>
          <select className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900" value={filters.module} onChange={(e) => setF('module', e.target.value)} data-testid="activity-filter-module">
            <option value="">All</option>
            {filterOptions.modules.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">From</label>
          <input type="date" className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900" value={filters.date_from} onChange={(e) => setF('date_from', e.target.value)} data-testid="activity-filter-from" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">To</label>
          <input type="date" className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900" value={filters.date_to} onChange={(e) => setF('date_to', e.target.value)} data-testid="activity-filter-to" />
        </div>
        <div className="flex items-end">
          <button onClick={resetFilters} className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 w-full">Reset</button>
        </div>
      </div>

      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">{error}</div>}

      <div className="clay-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Time</th>
                <th className="px-4 py-3 font-medium">Actor</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Action</th>
                <th className="px-4 py-3 font-medium">Module</th>
                <th className="px-4 py-3 font-medium">Entity</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No activity found.</td></tr>
              ) : logs.map((log) => (
                <tr key={log.id} className="border-t border-slate-100 dark:border-slate-800" data-testid="activity-row">
                  <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(log.timestamp, timeFormat)}</td>
                  <td className="px-4 py-3">{log.actor_name || '-'}</td>
                  <td className="px-4 py-3">{(log.actor_role || '-').replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3">{ACTION_LABELS[log.action] || log.action}</td>
                  <td className="px-4 py-3">{log.module || '-'}</td>
                  <td className="px-4 py-3 text-slate-500">{log.entity}{log.record_id ? ` (${String(log.record_id).slice(-6)})` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogPage;
